/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file voipCallerIdentity.js
 * @description
 * Pure helpers that turn the backend caller-identification payload
 * (`GET /api/voip/lookup`, socket `voip:incoming` / `voip:caller-identified`,
 * push `suggestedName`) into a display model for the call UI. Parity with the
 * admin web `features/Voip/callerIdentity.js`.
 *
 * A CONFIRMED match and an AI GUESS are always kept apart: a suggestion is never
 * promoted to the title, and an empty result never overwrites a confirmed contact.
 */

/** CallerMatch.type → flat i18n key + HU fallback. */
export const MATCH_TYPE_I18N = {
    contact: ['voip_caller_type_contact', 'Névjegy'],
    guest: ['voip_caller_type_guest', 'Vendég'],
    customer: ['voip_caller_type_customer', 'Ügyfél'],
    serviceCustomer: ['voip_caller_type_serviceCustomer', 'Szerviz ügyfél'],
    lead: ['voip_caller_type_lead', 'Lead'],
    partner: ['voip_caller_type_partner', 'Partner'],
    provider: ['voip_caller_type_provider', 'Szolgáltató'],
    user: ['voip_caller_type_user', 'Kolléga'],
    project: ['voip_caller_type_project', 'Projekt'],
    quote: ['voip_caller_type_quote', 'Ajánlat'],
    complaint: ['voip_caller_type_complaint', 'Panasz'],
    ticket: ['voip_caller_type_ticket', 'Jegy'],
    booking: ['voip_caller_type_booking', 'Foglalás'],
    order: ['voip_caller_type_order', 'Rendelés'],
    apollo: ['voip_caller_type_apollo', 'Apollo'],
    explee: ['voip_caller_type_explee', 'Explee'],
};

/**
 * Certainty scope of a match: a record of the CURRENT restaurant is "Biztos!"
 * (sure), anything found in other restaurants' data is only an estimate
 * ("Becslés"). A match without `scope` (legacy payload) counts as a restaurant one.
 * @param {Object|null} match CallerMatch.
 * @returns {('restaurant'|'global')}
 */
export const matchScope = (match) => (match?.scope === 'global' ? 'global' : 'restaurant');

/** Scope → flat i18n key + HU fallback of the certainty badge. */
export const SCOPE_I18N = {
    restaurant: ['voip_caller_sure', 'Biztos!'],
    global: ['voip_caller_estimate', 'Becslés'],
};

/** Statuses after which no further AI search will happen. */
const TERMINAL_STATUSES = ['matched', 'not_found', 'failed', 'no_credits', 'disabled', 'skipped'];

/** Statuses that show the subtle "Unknown number" label. */
const SILENT_STOP_STATUSES = ['not_found', 'failed', 'no_credits', 'disabled'];

/** An empty identity. */
export const EMPTY_IDENTITY = Object.freeze({
    contact: null,
    matches: [],
    suggestion: null,
    suggestionStatus: 'none',
});

/** Digits only (for tolerant phone comparison). */
const digits = (value) => String(value || '').replace(/\D/g, '');

/**
 * Whether two phone numbers are the same line (compares the last 9 digits).
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
export const samePhone = (a, b) => {
    const x = digits(a);
    const y = digits(b);

    if (!x || !y) {
        return false;
    }

    return x.slice(-9) === y.slice(-9);
};

/**
 * Normalizes any (possibly legacy) lookup / socket payload into an identity.
 * @param {Object|null} raw `{ match, contact, matches, suggestion, suggestionStatus }`.
 * @returns {{contact: Object|null, matches: Object[], suggestion: Object|null, suggestionStatus: string}}
 */
export const normalizeIdentity = (raw) => {
    const src = raw && typeof raw === 'object' ? raw : {};
    const best = src.match || src.contact || null;
    const matches = Array.isArray(src.matches) ? src.matches.filter(Boolean) : [];
    const contact = best && (best.name || best.id) ? best : (matches[0] || null);
    const list = matches.length ? matches : (contact ? [contact] : []);
    const suggestion = !contact && src.suggestion && (src.suggestion.name || src.suggestion.company)
        ? src.suggestion
        : null;
    let suggestionStatus = typeof src.suggestionStatus === 'string' ? src.suggestionStatus : 'none';

    if (contact) {
        suggestionStatus = 'matched';
    } else if (suggestion && suggestionStatus === 'none') {
        suggestionStatus = 'ready';
    }

    return {
        contact,
        matches: list,
        suggestion,
        suggestionStatus,
    };
};

/**
 * Whether an identity has neither a confirmed record nor an AI guess.
 * @param {Object} identity
 * @returns {boolean}
 */
export const isUnidentified = (identity) => !identity?.contact && !identity?.suggestion
    && !(identity?.matches || []).length;

/**
 * Whether it is worth asking the backend to run the AI web search.
 * @param {Object} identity
 * @returns {boolean}
 */
export const shouldRunAiSearch = (identity) => isUnidentified(identity)
    && !TERMINAL_STATUSES.includes(identity?.suggestionStatus || 'none')
    && identity?.suggestionStatus !== 'pending';

/**
 * Builds a provisional (unverified) suggestion from the push `suggestedName`.
 * @param {string} suggestedName Unverified AI guess carried by the wake push.
 * @returns {Object|null} A suggestion-shaped object or null.
 */
export const suggestionFromPush = (suggestedName) => {
    const name = String(suggestedName || '').trim();

    return name
        ? {name, company: '', kind: 'unknown', confidence: 0, reasoning: '', sources: [], unverified: true}
        : null;
};

/**
 * Merges a newer identity payload into the current one. An empty result never
 * overwrites a confirmed contact; an empty result never drops an existing guess.
 * @param {Object} prev Current identity.
 * @param {Object} incoming Raw payload (lookup / socket).
 * @returns {{contact: Object|null, matches: Object[], suggestion: Object|null, suggestionStatus: string}}
 */
export const mergeIdentity = (prev, incoming) => {
    const base = normalizeIdentity(prev);
    const next = normalizeIdentity(incoming);

    if (next.contact) {
        return next;
    }
    if (base.contact) {
        return base;
    }
    if (next.suggestion) {
        return next;
    }
    if (base.suggestion) {
        return base;
    }

    return next;
};

/**
 * Whether a `voip:caller-identified` payload belongs to the current call:
 * callId first, else the last-9-digit phone of the current remote party.
 * @param {{callId?: string, phone?: string}} payload Socket payload.
 * @param {{callId?: (string|null), remoteNumber?: string}} current Current call.
 * @returns {boolean}
 */
export const isForCurrentCall = (payload, current) => {
    if (!payload || !current) {
        return false;
    }
    const payloadId = payload.callId ? String(payload.callId) : '';
    const currentId = current.callId ? String(current.callId) : '';

    if (payloadId && currentId) {
        return payloadId === currentId;
    }

    return samePhone(payload.phone, current.remoteNumber);
};

/**
 * Builds the display model for the call UI.
 * @param {Object} identity `{contact, matches, suggestion, suggestionStatus}`.
 * @param {Object} [opts]
 * @param {string} [opts.number] The remote number (primary title when unconfirmed).
 * @param {boolean} [opts.searching] An AI search request is in flight.
 * @returns {{kind: ('confirmed'|'suggested'|'searching'|'unknown'), title: string, subtitle: string,
 *   typeKey: ([string, string]|null), scope: (('restaurant'|'global')|null), phone: string, link: string, extra: Object[], suggestion: (Object|null),
 *   confidencePct: (number|null), sources: Object[]}}
 */
export const describeCallerIdentity = (identity, opts = {}) => {
    const {
        contact, matches, suggestion, suggestionStatus,
    } = normalizeIdentity(identity);
    const number = String(opts.number || '');

    if (contact) {
        const subtitle = [contact.company, contact.detail].filter((v) => v && v !== contact.name).join(' · ');

        return {
            kind: 'confirmed',
            title: contact.name || number,
            subtitle,
            typeKey: MATCH_TYPE_I18N[contact.type] || null,
            scope: matchScope(contact),
            // The number stored on the matched record (may differ in format from the dialled one).
            phone: String(contact.phone || ''),
            link: contact.link || '',
            extra: matches.filter((m) => m !== contact && !(m.type === contact.type && m.id === contact.id)),
            suggestion: null,
            confidencePct: null,
            sources: [],
        };
    }

    const base = {
        title: number,
        subtitle: '',
        typeKey: null,
        scope: null,
        phone: '',
        link: '',
        extra: [],
    };

    if (suggestion) {
        const confidence = Number(suggestion.confidence);

        return {
            ...base,
            kind: 'suggested',
            suggestion,
            confidencePct: Number.isFinite(confidence) && confidence > 0
                ? Math.round(Math.min(confidence, 1) * 100)
                : null,
            sources: (Array.isArray(suggestion.sources) ? suggestion.sources : [])
                .filter((s) => /^https?:\/\//i.test(s?.url || '')),
        };
    }

    return {
        ...base,
        kind: opts.searching || suggestionStatus === 'pending' ? 'searching' : 'unknown',
        suggestion: null,
        confidencePct: null,
        sources: [],
    };
};

/**
 * Whether the UI should show the subtle "Unknown number" label.
 * @param {Object} identity
 * @returns {boolean}
 */
export const isSilentUnknown = (identity) => isUnidentified(identity)
    && SILENT_STOP_STATUSES.includes(identity?.suggestionStatus || 'none');

/**
 * Short "name · company" label for a stored call-log suggestion.
 * @param {Object|null} suggestion `callerSuggestion` from a call-log row.
 * @returns {string}
 */
export const suggestionLabel = (suggestion) => {
    if (!suggestion) {
        return '';
    }

    return [suggestion.name, suggestion.company].filter(Boolean).join(' · ');
};

/**
 * Muted history/recent label for an unknown number that carries an AI guess:
 * `? <name> (AI)`. Empty string when there is none.
 * @param {{callerSuggestion?: Object, suggestedName?: string}} row Call-log / recent-contact row.
 * @returns {string}
 */
export const historySuggestionText = (row) => {
    const label = suggestionLabel(row?.callerSuggestion) || String(row?.suggestedName || '').trim();

    return label ? `? ${label} (AI)` : '';
};
